import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, X, RotateCcw, Home, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

export default function Tafeltoets() {
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState("loading"); // loading | blocked | play | done
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [flash, setFlash] = useState(null);
  const [result, setResult] = useState(null);
  const lock = useRef(false);
  const finished = useRef(false);
  const scoreRef = useRef(0);

  const load = () => {
    finished.current = false; scoreRef.current = 0;
    setResult(null); setIdx(0); setScore(0); setPicked(null);
    api.get("/child/tafeltoets").then(({ data }) => {
      if (!data.available) { setPhase("blocked"); return; }
      setQs(data.questions); setPhase("play");
    }).catch((err) => toast.error(apiErr(err.response?.data?.detail)));
  };
  useEffect(load, []); // eslint-disable-line

  const finish = async (finalScore) => {
    if (finished.current) return;
    finished.current = true;
    try {
      const { data } = await api.post("/child/tafeltoets/finish", { correct: finalScore, total: qs.length, duration: 0 });
      setResult(data); setPhase("done"); refresh();
      if (data.perfect) { sfx.win(); setTimeout(() => navigate("/leerling/rumi-feest", { state: { unlockedNow: data.unlocked_now } }), 900); }
      else sfx.coin();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); setPhase("done"); }
  };

  const choose = (val) => {
    if (lock.current || finished.current) return;
    const q = qs[idx];
    const ok = val === q.answer;
    lock.current = true;
    setPicked(val);
    setFlash(ok ? "ok" : "no");
    if (ok) sfx.correct(); else sfx.wrong();
    const ns = score + (ok ? 1 : 0);
    setScore(ns); scoreRef.current = ns;
    setTimeout(() => {
      setFlash(null); setPicked(null); lock.current = false;
      if (idx + 1 >= qs.length) finish(ns);
      else setIdx(idx + 1);
    }, 480);
  };

  const muted = { color: "var(--t-muted)" };

  if (phase === "loading")
    return <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>;

  if (phase === "blocked")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-20 text-center">
          <Lock className="w-12 h-12 mx-auto mb-3" style={muted} />
          <h1 className="font-heading text-2xl font-black themed-heading">Nog niet beschikbaar</h1>
          <p className="mt-2 font-semibold" style={muted}>Deze toets is nu nog in test.</p>
          <Button onClick={() => navigate("/leerling")} className="mt-6 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="toets-back-home">Terug</Button>
        </div>
      </ThemedShell>
    );

  if (phase === "done") {
    const sc = result?.score ?? score;
    const tot = result?.total ?? qs.length;
    const perfect = result?.perfect;
    return (
      <ThemedShell>
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-16">
          <ThemedCard className="p-8 text-center" data-testid="toets-result">
            <div className="text-6xl mb-3">{perfect ? "🏆" : sc / tot >= 0.8 ? "💪" : theme.mascot}</div>
            <h1 className="font-heading text-3xl font-black themed-heading">{perfect ? "PERFECT! 100%!" : "Goed gewerkt!"}</h1>
            <p className="mt-2 font-semibold" style={muted}>Je had <b>{sc}</b> van de <b>{tot}</b> sommen goed</p>
            <div className="my-6 font-heading text-6xl font-black themed-heading" data-testid="toets-score">{sc}/{tot}</div>
            {!perfect && <p className="font-semibold mb-2" style={{ color: "var(--t-primary)" }}>Haal 100% om de geheime held vrij te spelen! 🎉</p>}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button onClick={() => { setPhase("loading"); load(); }} className="flex-1 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="toets-again-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Opnieuw</Button>
              <Button onClick={() => navigate("/leerling")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="toets-home-btn"><Home className="w-4 h-4 mr-1.5" /> Dashboard</Button>
            </div>
          </ThemedCard>
        </div>
      </ThemedShell>
    );
  }

  const q = qs[idx] || { options: [] };
  const pct = Math.round((idx / qs.length) * 100);
  return (
    <ThemedShell>
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-16">
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" onClick={() => navigate("/leerling")} className="rounded-full font-bold px-3" style={{ color: "var(--t-text)" }} data-testid="toets-exit-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <span className="font-heading font-extrabold themed-heading">Tafeltoets 🎯</span>
          <span className="font-heading font-extrabold text-sm" data-testid="toets-progress" style={muted}>{idx + 1}/{qs.length}</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden mb-8" style={{ background: "var(--t-surface2)" }}>
          <motion.div className="h-full rounded-full" style={{ background: "var(--t-accent)" }} animate={{ width: `${pct}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.16 }}>
            <ThemedCard className="p-8 text-center" style={flash === "ok" ? { borderColor: "var(--t-success)" } : flash === "no" ? { borderColor: "var(--t-primary)" } : {}}>
              <div className="font-heading text-5xl font-black themed-heading mb-7" data-testid="toets-question">{q.a} × {q.b} = ?</div>
              <div className="grid grid-cols-2 gap-3">
                {q.options.map((o, j) => {
                  const isAns = picked != null && o === q.answer;
                  const isWrong = picked === o && o !== q.answer;
                  return (
                    <button key={j} onClick={() => choose(o)} data-testid={`toets-opt-${o}`}
                      className="min-h-16 py-3 rounded-2xl font-heading font-black text-2xl border-2 transition-all active:scale-95"
                      style={isAns ? { background: "var(--t-success)", color: "#fff", borderColor: "var(--t-success)" }
                        : isWrong ? { background: "var(--t-primary)", color: "#fff", borderColor: "var(--t-primary)" }
                        : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                      {o}
                    </button>
                  );
                })}
              </div>
            </ThemedCard>
          </motion.div>
        </AnimatePresence>

        {flash && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed inset-x-0 top-24 flex justify-center pointer-events-none z-50">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: flash === "ok" ? "var(--t-success)" : "var(--t-primary)", color: "#fff" }}>
              {flash === "ok" ? <Check className="w-8 h-8" strokeWidth={3} /> : <X className="w-8 h-8" strokeWidth={3} />}
            </div>
          </motion.div>
        )}
      </div>
    </ThemedShell>
  );
}
